import { useEffect, useRef, useState } from 'react';
import { adBanners } from '@/mocks/ads';
import { fetchSponsoredAds, adsForPage, type SponsoredAd } from '@/lib/ads';

interface DisplayAd {
  id: string;
  sponsor: string;
  title: string;
  description: string;
  category: string;
  mediaType: 'image' | 'video';
  mediaUrl: string;
  linkUrl: string | null;
  ctaLabel: string;
}

const toDisplay = (ads: SponsoredAd[]): DisplayAd[] =>
  ads.map((ad) => ({
    id: ad.id,
    sponsor: ad.sponsor,
    title: ad.title,
    description: ad.description || '',
    category: ad.category || '',
    mediaType: ad.media_type,
    mediaUrl: ad.media_url,
    linkUrl: ad.link_url,
    ctaLabel: ad.cta_label || 'Découvrir',
  }));

const mockToDisplay = (): DisplayAd[] =>
  adBanners.map((ad) => ({
    id: ad.id,
    sponsor: ad.sponsor,
    title: ad.tagline,
    description: ad.description,
    category: ad.category,
    mediaType: 'image',
    mediaUrl: ad.image_url,
    linkUrl: null,
    ctaLabel: ad.cta_label,
  }));

interface AdsBannerProps {
  /** Clé de la page courante (voir AD_PAGE_TARGETS). Par défaut : 'media'. */
  pageKey?: string;
}

function AdsBanner({ pageKey = 'media' }: AdsBannerProps) {
  const [ads, setAds] = useState<DisplayAd[]>([]);
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);
  const paused = useRef(false);

  useEffect(() => {
    let mounted = true;
    fetchSponsoredAds()
      .then((data) => {
        if (!mounted) return;
        // Filtre selon la page courante.
        const forPage = adsForPage(data, pageKey);
        if (forPage.length > 0) {
          setAds(toDisplay(forPage));
        } else if (pageKey === 'media') {
          // Sur la page Média uniquement, on conserve le visuel de démo si aucune vraie pub.
          setAds(mockToDisplay());
        } else {
          setAds([]);
        }
      })
      .catch(() => {
        if (mounted) setAds(pageKey === 'media' ? mockToDisplay() : []);
      });
    return () => {
      mounted = false;
    };
  }, [pageKey]);

  useEffect(() => {
    if (ads.length <= 1) return;
    timer.current = window.setInterval(() => {
      if (paused.current) return;
      setIndex((prev) => (prev + 1) % ads.length);
    }, 3000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [ads.length]);

  if (ads.length === 0) return null;

  const ad = ads[index];

  const goTo = (i: number) => {
    setIndex((i + ads.length) % ads.length);
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-bold text-base md:text-lg text-foreground-50 flex items-center gap-2">
          <i className="ri-megaphone-line text-accent-400" />
          Publicités sponsorisées
        </h2>
        <span className="text-xs text-foreground-400">Espace partenaires</span>
      </div>

      <div
        className="relative w-full h-[180px] md:h-[240px] rounded-lg overflow-hidden border border-background-200/70 bg-background-950"
        onMouseEnter={() => {
          paused.current = true;
        }}
        onMouseLeave={() => {
          paused.current = false;
        }}
      >
        {/* Visuel : image ou vidéo */}
        {ad.mediaType === 'video' ? (
          <video
            src={ad.mediaUrl}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        ) : (
          <img
            src={ad.mediaUrl}
            alt={ad.sponsor}
            title={`${ad.sponsor} — ${ad.title}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        )}
        {/* Dégradé pour lisibilité */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />

        {/* Contenu */}
        <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-10 max-w-xl">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground-50/80 mb-2">
            <span className="w-2 h-2 rounded-full bg-accent-400" />
            {ad.category}
          </span>
          <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 leading-tight mb-1">
            {ad.title}
          </h3>
          <p className="text-sm text-foreground-50/80 leading-relaxed mb-4 hidden sm:block">
            {ad.description}
          </p>
          <div className="flex items-center gap-3">
            <a
              href={ad.linkUrl || '#'}
              target={ad.linkUrl ? '_blank' : undefined}
              rel={ad.linkUrl ? 'nofollow noopener noreferrer' : undefined}
              onClick={(e) => {
                if (!ad.linkUrl) e.preventDefault();
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              {ad.ctaLabel}
              <i className="ri-arrow-right-line" />
            </a>
            <span className="text-xs font-semibold text-foreground-50">par {ad.sponsor}</span>
          </div>
        </div>

        {/* Flèches */}
        <button
          onClick={() => goTo(index - 1)}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors z-20"
          aria-label="Publicité précédente"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          onClick={() => goTo(index + 1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors z-20"
          aria-label="Publicité suivante"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>

        {/* Points */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20">
          {ads.map((b, i) => (
            <button
              key={b.id}
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? 'w-6 bg-primary-500' : 'w-1.5 bg-foreground-50/60'
              }`}
              aria-label={`Aller à la publicité ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default AdsBanner;